import React from 'react';
import { useTranslation } from '@shared/core';
import { EventGroupCodeBannerContainer } from '@apps/guest/packages/layout-engine/components/EventGroupCodeBanner';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { useMarriottRoomBlockBanner } from '../../hooks/useMarriottRoomBlockBanner';
import { LazyMarriottRoomBlockBanner } from './MarriottRoomBlockBanner.lazy';

export type MarriottRoomBlockBannerContainerProps = Readonly<{
  event: GuestSiteEvent;
  eventHandle: string;
  /** True for host preview, native webview and full-page password/OTP gates: no Marriott ad there. */
  isSuppressed: boolean;
}>;

/**
 * One promo strip at most. When both qualify, Marriott wins unless the treatment payload sets
 * `weddingCodeFirst`. Renders nothing until resolved, so neither banner flashes in and is replaced.
 */
export const MarriottRoomBlockBannerContainer: React.FC<MarriottRoomBlockBannerContainerProps> = ({ event, eventHandle, isSuppressed }) => {
  const { tq } = useTranslation('accommodationsGuest');
  const marriott = useMarriottRoomBlockBanner({ eventId: event.id, eventHandle, isSuppressed });

  if (!marriott.isResolved) {
    return null;
  }

  const marriottStrip = marriott.isVisible ? (
    <LazyMarriottRoomBlockBanner
      placeId={marriott.placeId}
      onViewed={marriott.onViewed}
      message={marriott.message}
      actionLabel={marriott.actionLabel}
      closeLabel={tq`Close room block banner`}
      onBook={marriott.onBook}
      onClose={marriott.onClose}
    />
  ) : null;

  if (marriottStrip && !marriott.weddingCodeFirst) {
    return marriottStrip;
  }

  return (
    <EventGroupCodeBannerContainer
      eventHandle={eventHandle}
      event={event}
      fallback={marriottStrip}
    />
  );
};

MarriottRoomBlockBannerContainer.displayName = 'MarriottRoomBlockBannerContainer';
